/**
 * PdfPageThumbnail - Individual page thumbnail with selection checkbox
 */
import { CheckIcon } from '@heroicons/react/20/solid';
import { memo, useState } from 'react';
import { Page } from 'react-pdf';

import { cn } from '@/lib/utils';

interface PdfPageThumbnailProps {
  /** Page number (1-indexed) */
  pageNumber: number;
  /** Whether this page is selected */
  isSelected: boolean;
  /** Callback when selection changes */
  onToggle: (pageNumber: number) => void;
  /** Width of the thumbnail in pixels */
  width?: number;
}

export const PdfPageThumbnail = memo(function PdfPageThumbnail({
  pageNumber,
  isSelected,
  onToggle,
  width = 100,
}: PdfPageThumbnailProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleClick = () => {
    onToggle(pageNumber);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle(pageNumber);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={cn(
        'relative flex flex-col items-center gap-1.5 p-1.5 rounded-lg transition-all duration-150',
        'hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2',
        isSelected && 'bg-indigo-50 hover:bg-indigo-100',
      )}
      aria-pressed={isSelected}
      aria-label={`Page ${pageNumber}${isSelected ? ', selected' : ''}`}
    >
      {/* Thumbnail container */}
      <div
        className={cn(
          'relative rounded border-2 overflow-hidden bg-white shadow-sm transition-all duration-150',
          isSelected
            ? 'border-indigo-500 ring-2 ring-indigo-200'
            : 'border-gray-200 hover:border-gray-300',
        )}
        style={{ width, height: width * 1.4 }}
      >
        {/* Loading skeleton */}
        {!isLoaded && !hasError && (
          <div className="absolute inset-0 bg-gray-100 animate-pulse" />
        )}

        {/* Error state */}
        {hasError && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
            <span className="text-xs text-gray-400">Error</span>
          </div>
        )}

        {/* PDF Page */}
        <Page
          pageNumber={pageNumber}
          width={width - 4}
          renderTextLayer={false}
          renderAnnotationLayer={false}
          onRenderSuccess={() => setIsLoaded(true)}
          onRenderError={() => setHasError(true)}
          className="pointer-events-none"
        />

        {/* Selection indicator */}
        {isSelected && (
          <div className="absolute top-1 right-1 w-5 h-5 bg-indigo-500 rounded-full flex items-center justify-center shadow-sm">
            <CheckIcon className="w-3.5 h-3.5 text-white" />
          </div>
        )}
      </div>

      {/* Page number */}
      <span
        className={cn(
          'text-xs font-medium tabular-nums',
          isSelected ? 'text-indigo-700' : 'text-gray-600',
        )}
      >
        {pageNumber}
      </span>
    </button>
  );
});

export default PdfPageThumbnail;
